/**
 * Campaign / promotion status — the single source of truth for "what state is
 * this campaign actually in, right now".
 *
 * A campaign's rendered status is its base status (from the mock pool today, the
 * API tomorrow) reconciled with two runtime rules:
 *   1. a pause/resume **override** the owner made this session (Redux slice
 *      `campaignStatus`), and
 *   2. the **expiry rule**: a campaign that *once ran* (running or paused — not a
 *      mere suggestion) and whose expiry deadline has passed is treated as ended.
 *
 * Keeping this here (not inline in each page) means the detail dispatchers and
 * the list browsers resolve status identically, and the move to a real API only
 * swaps the *source* of the override (a thunk) and the seed — not this logic.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { setCampaignStatus, type CampaignStatusOverride } from '@/store/slices/campaignStatus';
import type { CampaignStatus } from '../mock-data/types';

/**
 * True once a *once-run* campaign has passed its expiry. A suggestion is never
 * "expired into ended" — it simply stops being suggested — so the rule is scoped
 * to running/paused.
 */
export const isExpired = (status: CampaignStatus, expiresAt: number | undefined, now: number): boolean =>
  (status === 'running' || status === 'paused') && expiresAt != null && expiresAt <= now;

/**
 * The status to actually render: a runtime override wins over the base status,
 * then the expiry rule applies. `now` is injected so callers own the clock — a
 * live-ticking list passes a ticking `now` (statuses flip the instant they
 * expire); a detail page passes `Date.now()` once per render.
 */
export const resolveCampaignStatus = (
  base: CampaignStatus,
  expiresAt: number | undefined,
  overrideStatus: CampaignStatus | undefined,
  now: number,
): CampaignStatus => {
  const status = overrideStatus ?? base;
  return isExpired(status, expiresAt, now) ? 'ended' : status;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/**
 * Format an epoch timestamp as the "Mon DD, HH:MM" label the cards use for
 * "Paused on …" / "Ended on …" (matches the hand-written mock statusAt strings).
 */
export const formatStatusAt = (epochMs: number): string => {
  const d = new Date(epochMs);
  return `${MONTHS[d.getMonth()]} ${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// --- React bindings ----------------------------------------------------------

/** The runtime override map (campaign/promotion id → {status, at}). */
export const useCampaignOverrides = (): Record<string, CampaignStatusOverride> =>
  useAppSelector((state) => state.campaignStatus.overrides);

export type CampaignStatusActions = {
  /** Activate a suggested campaign/promotion (status → running). */
  activate: (id: string) => void;
  pause: (id: string) => void;
  /** Resume a campaign; refused (no-op) if it has already passed its expiry. */
  resume: (id: string, opts?: { expiresAt?: number }) => void;
};

/**
 * Activate / pause / resume actions. Today they write the override slice; when
 * the API lands they become thunks that POST then update the cache — callers are
 * unaffected. `at` (the change time) is stamped here so the reducer stays pure.
 */
export const useCampaignStatusActions = (): CampaignStatusActions => {
  const dispatch = useAppDispatch();

  // Activating a suggestion flips it straight to running. Unlike resume there is
  // no expiry guard: a suggestion's `expiresAt` is its offer countdown (in the
  // future), not a spent running deadline.
  const activate = React.useCallback(
    (id: string) => {
      dispatch(setCampaignStatus({ id, status: 'running', at: Date.now() }));
    },
    [dispatch],
  );

  const pause = React.useCallback(
    (id: string) => {
      dispatch(setCampaignStatus({ id, status: 'paused', at: Date.now() }));
    },
    [dispatch],
  );

  const resume = React.useCallback(
    (id: string, opts?: { expiresAt?: number }) => {
      // Enforce "cannot resume past expiry" at the action boundary too, so the
      // guarantee holds even if a stale view briefly still shows a Resume button.
      if (opts?.expiresAt != null && opts.expiresAt <= Date.now()) return;
      dispatch(setCampaignStatus({ id, status: 'running', at: Date.now() }));
    },
    [dispatch],
  );

  return { activate, pause, resume };
};
